import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { api } from '../../api';
import { lerCoordenadas, tiposDe } from '../../componentes/formatos';
import { NOME_NIVEL, NOME_PROPRIEDADE, NOME_STATUS_ATIVO, type Categoria, type Modelo, type Nivel } from './comum';

export interface DadosAtivo {
  id?: string; versao?: number; local_id: string; categoria_id: string; fabricante: string | null; modelo: string | null; apelido: string;
  numero_serie: string | null; ip: string | null; mac: string | null; instalado_em: string | null; garantia_ate: string | null;
  propriedade: string; valor_reposicao: string | number | null; ativo_pai_id: string | null; status: string; observacoes: string | null;
  latitude?: number | null; longitude?: number | null; comprimento_m?: number | string | null;
  nivel?: Nivel | null; quantidade?: number | string | null;
}
interface ClienteBusca { id: string; razao_social: string; nome_fantasia: string | null }
interface LocalItem { id: string; nome: string; ativo: boolean }

export function FormAtivo({ inicial, localFixo, categoriaInicial, aoSalvar, aoCancelar }: {
  inicial?: DadosAtivo & { cliente_id?: string; cliente?: string; local?: string };
  localFixo?: { id: string; nome: string; clienteId: string; cliente: string };
  /** código da categoria já escolhida (ex.: 'agrupador.via' no botão "+ via" do parque) */
  categoriaInicial?: string;
  aoSalvar: (id: string) => void; aoCancelar: () => void;
}) {
  const [f, setF] = useState<DadosAtivo>(inicial ?? {
    local_id: localFixo?.id ?? '', categoria_id: '', fabricante: '', modelo: '', apelido: '', numero_serie: '', ip: '', mac: '',
    instalado_em: '', garantia_ate: '', propriedade: 'cliente', valor_reposicao: '', ativo_pai_id: null, status: 'em_operacao', observacoes: '',
  });
  const [categorias, setCategorias] = useState<Categoria[]>([]);
  const [modelos, setModelos] = useState<Modelo[]>([]);
  const [possiveisPais, setPossiveisPais] = useState<{ id: string; apelido: string }[]>([]);
  // escolha de cliente → local (quando o formulário não vem de um local)
  const [clienteId, setClienteId] = useState(inicial?.cliente_id ?? localFixo?.clienteId ?? '');
  const [clienteNome, setClienteNome] = useState(inicial?.cliente ?? localFixo?.cliente ?? '');
  const [busca, setBusca] = useState('');
  const [achados, setAchados] = useState<ClienteBusca[]>([]);
  const [locais, setLocais] = useState<LocalItem[]>([]);
  const [coord, setCoord] = useState(inicial?.latitude != null ? `${inicial.latitude}, ${inicial.longitude}` : '');
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const set = (k: keyof DadosAtivo) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }));

  useEffect(() => {
    api<Categoria[]>('/categorias-ativo').then((cs) => {
      setCategorias(cs);
      const ini = categoriaInicial && cs.find((c) => c.codigo === categoriaInicial);
      if (ini) setF((x) => (x.categoria_id ? x : { ...x, categoria_id: ini.id }));
    }).catch(() => {});
  }, [categoriaInicial]);
  useEffect(() => {
    if (!f.categoria_id) return setModelos([]);
    api<Modelo[]>(`/modelos-ativo?categoria_id=${f.categoria_id}`).then(setModelos).catch(() => {});
  }, [f.categoria_id]);
  useEffect(() => {
    if (!busca.trim() || clienteId) return setAchados([]);
    const t = setTimeout(() => api<{ itens: ClienteBusca[] }>(`/clientes?busca=${encodeURIComponent(busca)}`).then((r) => setAchados(r.itens.slice(0, 8))).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [busca, clienteId]);
  useEffect(() => {
    if (!clienteId || localFixo) return;
    api<{ locais: LocalItem[] }>(`/clientes/${clienteId}`).then((c) => setLocais(c.locais.filter((l) => l.ativo))).catch(() => {});
  }, [clienteId, localFixo]);
  useEffect(() => {
    if (!f.local_id) return setPossiveisPais([]);
    api<{ itens: { id: string; apelido: string }[] }>(`/ativos?local_id=${f.local_id}&por_pagina=500`).then((r) => setPossiveisPais(r.itens.filter((a) => a.id !== inicial?.id))).catch(() => {});
  }, [f.local_id, inicial?.id]);

  const grupos = useMemo(() => {
    const raizes = categorias.filter((c) => !c.pai_id);
    return raizes.map((r) => ({ raiz: r, filhas: categorias.filter((c) => c.pai_id === r.id) }));
  }, [categorias]);
  const fabricantes = useMemo(() => [...new Set(modelos.map((m) => m.fabricante))], [modelos]);
  const modelosDoFabricante = modelos.filter((m) => !f.fabricante || m.fabricante.toLowerCase() === f.fabricante.toLowerCase());
  const daMaza = f.propriedade !== 'cliente';
  // via de acesso / setor: só agrupa o que está dentro dela (sem modelo, série, IP, MAC, valor)
  const agrupador = categorias.find((c) => c.id === f.categoria_id)?.agrupador === true;
  const codigoCategoria = categorias.find((c) => c.id === f.categoria_id)?.codigo ?? '';
  // coordenada: setor/via e caixa de poste (onde a foto do técnico é conferida); comprimento: zona de cerca
  const pedeCoordenada = agrupador || codigoCategoria === 'rede.caixa_poste' || coord.trim() !== '';
  // 0030: nível (null = segue a categoria) e quantidade só para componente/consumível
  const nivelCategoria = categorias.find((c) => c.id === f.categoria_id)?.nivel ?? 'essencial';
  const nivelEfetivo: Nivel = (f.nivel ?? nivelCategoria) as Nivel;
  const qtd = Math.max(1, Math.round(Number(f.quantidade ?? 1)) || 1);
  const emLote = !agrupador && nivelEfetivo !== 'essencial' && qtd > 1;
  const pedeComprimento = codigoCategoria === 'perimetro.zona_cerca' || (f.comprimento_m != null && f.comprimento_m !== '');

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setErro(null);
    if (!f.local_id) return setErro('Escolha o cliente e o parque onde o equipamento está.');
    let geo: { latitude: number | null; longitude: number | null } = { latitude: null, longitude: null };
    if (coord.trim()) {
      const c = lerCoordenadas(coord);
      if (!c) return setErro('Coordenadas inválidas. Cole como aparece no Google Maps, ex.: -22.7253, -47.6476');
      geo = c;
    }
    const comp = f.comprimento_m === '' || f.comprimento_m == null ? null : Math.round(Number(String(f.comprimento_m).replace(',', '.')));
    if (comp !== null && !(comp >= 1 && comp <= 100000)) return setErro('Comprimento inválido (metros, entre 1 e 100.000).');
    setSalvando(true);
    const corpo = {
      local_id: f.local_id, categoria_id: f.categoria_id, fabricante: f.fabricante || null, modelo: f.modelo || null, apelido: f.apelido,
      numero_serie: f.numero_serie || null, ip: f.ip || null, mac: f.mac || null, instalado_em: f.instalado_em || null, garantia_ate: f.garantia_ate || null,
      propriedade: f.propriedade, valor_reposicao: f.valor_reposicao === '' || f.valor_reposicao === null ? null : Number(String(f.valor_reposicao).replace(',', '.')),
      ativo_pai_id: f.ativo_pai_id || null, status: f.status, observacoes: f.observacoes || null, ...geo, comprimento_m: comp,
      nivel: agrupador ? null : (f.nivel ?? null), quantidade: agrupador || nivelEfetivo === 'essencial' ? 1 : qtd,
      ...(emLote ? { numero_serie: null, ip: null, mac: null } : {}),
      ...(agrupador ? { fabricante: null, modelo: null, numero_serie: null, ip: null, mac: null, propriedade: 'cliente', valor_reposicao: null } : {}),
    };
    try {
      if (inicial?.id) { await api(`/ativos/${inicial.id}`, { metodo: 'PATCH', corpo: { ...corpo, versao: inicial.versao } }); aoSalvar(inicial.id); }
      else { const r = await api<{ id: string }>('/ativos', { metodo: 'POST', corpo }); aoSalvar(r.id); }
    } catch (err) { setErro(err instanceof Error ? err.message : 'Não foi possível salvar.'); }
    finally { setSalvando(false); }
  }

  return (
    <form className="form" onSubmit={enviar}>
      {localFixo ? (
        <p className="contexto-form">{localFixo.cliente} · <strong>{localFixo.nome}</strong></p>
      ) : (
        <div className="linha-2">
          <div className="campo"><span className="rotulo-campo">Cliente</span>
            {clienteId ? (
              <div className="campo-mascarado"><span>{clienteNome}</span>
                <button type="button" className="btn-link" onClick={() => { setClienteId(''); setClienteNome(''); setLocais([]); setF((x) => ({ ...x, local_id: '', ativo_pai_id: null })); }}>trocar</button></div>
            ) : (
              <div className="autocompletar">
                <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Digite o nome do cliente" autoFocus />
                {achados.length > 0 && (
                  <ul role="listbox">{achados.map((c) => (
                    <li key={c.id}><button type="button" onClick={() => { setClienteId(c.id); setClienteNome(c.nome_fantasia ?? c.razao_social); setBusca(''); }}>
                      {c.razao_social}{c.nome_fantasia && <small> · {c.nome_fantasia}</small>}</button></li>))}
                  </ul>
                )}
              </div>
            )}
          </div>
          <label>Parque
            <select required value={f.local_id} onChange={(e) => setF((x) => ({ ...x, local_id: e.target.value, ativo_pai_id: null }))} disabled={!clienteId}>
              <option value="">{clienteId ? (locais.length ? 'Escolha o parque' : 'Cliente sem parques cadastrados') : 'Escolha o cliente primeiro'}</option>
              {locais.map((l) => <option key={l.id} value={l.id}>{l.nome}</option>)}
            </select>
          </label>
        </div>
      )}
      <div className="linha-2">
        <label>Categoria
          <select required value={f.categoria_id} onChange={set('categoria_id')}>
            <option value="">Escolha</option>
            {grupos.map(({ raiz, filhas }) => (
              <optgroup key={raiz.id} label={raiz.nome}>
                <option value={raiz.id}>{raiz.nome} (geral)</option>
                {filhas.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
              </optgroup>
            ))}
          </select>
        </label>
        <label>Como a equipe chama
          <input required minLength={2} maxLength={120} value={f.apelido} onChange={set('apelido')} placeholder='Ex.: "Portão veículos entrada", "Câmera 07 guarita"' autoFocus={!!localFixo} />
        </label>
      </div>
      {agrupador && <p className="nota">Via/setor é só um agrupador: cadastre aqui o nome (ex.: “Via Entrada Morador”) e depois pendure nele os portões, motores, sensores e interfones. Ele não leva modelo, série, IP nem etiqueta.</p>}
      {!agrupador && <>
      <div className="linha-2">
        <label>Fabricante
          <input list="lista-fabricantes" maxLength={80} value={f.fabricante ?? ''} onChange={set('fabricante')} placeholder="Ex.: Hikvision, PPA, Control iD" />
          <datalist id="lista-fabricantes">{fabricantes.map((x) => <option key={x} value={x} />)}</datalist>
        </label>
        <label>Modelo
          <input list="lista-modelos" maxLength={80} value={f.modelo ?? ''} onChange={set('modelo')} />
          <datalist id="lista-modelos">{modelosDoFabricante.map((m) => <option key={m.id} value={m.modelo} />)}</datalist>
        </label>
      </div>
      <div className="linha-2">
        <label>Nível
          <select value={f.nivel ?? ''} onChange={(e) => setF((x) => ({ ...x, nivel: (e.target.value || null) as Nivel | null }))}>
            <option value="">Seguir a categoria ({NOME_NIVEL[nivelCategoria as Nivel]})</option>
            {(Object.keys(NOME_NIVEL) as Nivel[]).map((n) => <option key={n} value={n}>{NOME_NIVEL[n]}</option>)}
          </select>
          <small className="ajuda">Essencial aparece na árvore e entra na preventiva. Componente/consumível fica oculto e pode ser cadastrado em quantidade.</small>
        </label>
        {nivelEfetivo !== 'essencial' && <label>Quantidade
          <input type="number" min={1} max={999} value={f.quantidade ?? 1} onChange={set('quantidade')} />
          <small className="ajuda">Ex.: 5 fontes iguais num registro só (sem série/IP por unidade).</small>
        </label>}
      </div>
      {!emLote && <div className="linha-3-igual">
        <label>Nº de série<input maxLength={80} value={f.numero_serie ?? ''} onChange={set('numero_serie')} /></label>
        <label>IP<input maxLength={45} value={f.ip ?? ''} onChange={set('ip')} placeholder="192.168.0.10" inputMode="decimal" /></label>
        <label>MAC<input maxLength={17} value={f.mac ?? ''} onChange={set('mac')} placeholder="00:1A:2B:3C:4D:5E" /></label>
      </div>}
      </>}
      <div className="linha-3-igual">
        <label>Instalado em<input type="date" value={f.instalado_em ?? ''} onChange={set('instalado_em')} /></label>
        <label>Garantia até<input type="date" value={f.garantia_ate ?? ''} onChange={set('garantia_ate')} /></label>
        <label>Situação
          <select value={f.status} onChange={set('status')}>{tiposDe(NOME_STATUS_ATIVO).map(([v, r]) => <option key={v} value={v}>{r}</option>)}</select>
        </label>
      </div>
      {!agrupador && <div className="linha-2">
        <label>De quem é
          <select value={f.propriedade} onChange={set('propriedade')}>{tiposDe(NOME_PROPRIEDADE).map(([v, r]) => <option key={v} value={v}>{r}</option>)}</select>
        </label>
        <label>Valor de reposição (R$){daMaza ? ' *' : ''}
          <input inputMode="decimal" value={f.valor_reposicao ?? ''} onChange={set('valor_reposicao')} required={daMaza} placeholder={daMaza ? 'Obrigatório para equipamento da MAZA' : 'Opcional'} />
        </label>
      </div>}
      {possiveisPais.length > 0 && (
        <label>Faz parte de (equipamento pai)
          <select value={f.ativo_pai_id ?? ''} onChange={(e) => setF((x) => ({ ...x, ativo_pai_id: e.target.value || null }))}>
            <option value="">Nenhum (equipamento principal)</option>
            {possiveisPais.map((a) => <option key={a.id} value={a.id}>{a.apelido}</option>)}
          </select>
          <small className="ajuda">Ex.: o motor faz parte do portão, que faz parte da “Via Entrada Morador”.</small>
        </label>
      )}
      {(pedeCoordenada || pedeComprimento) && <div className="linha-2">
        {pedeCoordenada && <label>Coordenadas (opcional)
          <input value={coord} onChange={(e) => setCoord(e.target.value)} placeholder="-22.7253, -47.6476" />
          <small className="ajuda">Num condomínio grande, a foto do técnico passa a mostrar a distância a este ponto, não só à portaria. Google Maps: botão direito no lugar → copiar os números.</small>
        </label>}
        {pedeComprimento && <label>Comprimento do trecho (m)
          <input inputMode="numeric" value={f.comprimento_m ?? ''} onChange={set('comprimento_m')} placeholder="Ex.: 180" />
        </label>}
      </div>}
      <label>Observações<textarea rows={2} maxLength={4000} value={f.observacoes ?? ''} onChange={set('observacoes')} /></label>
      {erro && <p className="alerta" role="alert">{erro}</p>}
      <div className="acoes-form">
        <button type="button" className="btn-fantasma" onClick={aoCancelar}>Cancelar</button>
        <button className="btn" disabled={salvando || f.apelido.trim().length < 2 || !f.categoria_id}>{salvando ? 'Salvando…' : 'Salvar'}</button>
      </div>
    </form>
  );
}
